import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { api } from './client';

export type EmailStatus = 'queued' | 'sent' | 'failed' | 'bounced' | 'delivered';

export interface AdminEmailLog {
  id: string;
  toUserId: string | null;
  toEmail: string;
  templateKey: string;
  subject: string;
  bodyPreview: string | null;
  status: EmailStatus;
  failureReason: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface ListEmailsQuery {
  limit?: number;
  offset?: number;
  search?: string;
  templateKey?: string;
  status?: EmailStatus;
  toUserId?: string;
}

function buildQS(q: ListEmailsQuery): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function emailsKey(query: ListEmailsQuery = {}): QueryKey {
  return ['admin', 'emails', query];
}

export function useAdminEmails(query: ListEmailsQuery) {
  return useQuery({
    queryKey: emailsKey(query),
    queryFn: () =>
      api<{ total: number; items: AdminEmailLog[] }>(`/api/admin/emails${buildQS(query)}`),
    placeholderData: (prev) => prev,
  });
}

/**
 * Operator-requested email resend.
 *
 * Today the server returns 501 because `core.email_log` only persists a
 * 200-char body preview and we can't rebuild a faithful envelope from it.
 * The hook stays exported so a future typed-resend pipeline can wire it
 * back without code churn — the admin UI hides the button for now.
 */
export function useResendEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<{ accepted: true }>(`/api/admin/emails/${id}/resend`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'emails'] });
    },
  });
}
